import React, {
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
} from 'react';
import useTranslation from 'hooks/useTranslation';
import ErrorHandlerService from 'services/error-handler/service';
import { Message } from '@ui';
import { signOut } from 'redux/actions/auth';
import { TFunction } from 'i18next';
import { RoutePaths } from 'routes/routes';
import { StateModel } from 'redux/reducers';
import { crmAdapter } from 'apiAdapters/crm/crmAdapter';
import { useNavigate } from 'react-router-dom';
import { renderTemplate } from 'helpers/crmHelpers';
import { applicationsAPI } from 'api/application/applicationsAPI';
import { FieldIssueModel } from 'components/Tables/TableTemplates/IntakeForm/FieldIssueTable';
import { Step, StepStatus } from 'components/Additional/StepsCard';
import { TEMPLATE_FIELD_LABELS } from 'constants/intakeForm';
import {
  selectApplication,
  setActiveApplication,
} from 'redux/actions/applications';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationCommentsStatus } from 'enums/onboarding/crm';
import { CHANGE_VIEW_TYPES, changeView } from 'redux/actions/view';
import {
  AdditionalQuestionAnswer,
  FieldIssue,
  FieldIssueWithTemplate,
  FieldTemplate,
  intakeFormAPI,
  IntakeFormFields,
  IntakeFormFromQuery,
  IntakeFormStatus,
  IntakeFormStepStatus,
  SupplementaryQuestionsFormFromQuery,
} from 'api/crm/intakeFormAPI';

type LayoutValue = 'intake-form' | 'status' | null;

export const SUPPLEMENTARY_QUESTIONS_STEP_KEY_BASE =
  'supplementary_questions_form-';

export interface ApplicationReviewModel {
  allCommentsCount: number;
  activeCommentsCount: number;
  resolvedCommentsCount: number;
  fieldIssues: FieldCommentModel[];
}

export interface FieldCommentModel {
  stepName: string;
  fieldComments: FieldIssueModel[];
}

interface EntityIntakeFormContextData {
  intakeForm: IntakeFormFromQuery | null;
  setIntakeForm: (intakeForm: IntakeFormFromQuery) => void;
  steps: Step[];
  applicationReview: ApplicationReviewModel | null;
  activeStepKey: string | null;
  selectStep: (key: string) => void;
  loadingIntakeForm: boolean;
  fetchAndStoreIntakeForm: () => Promise<void>;
  navigateToNextStep: () => void;
  navigateToPreviousStep: () => void;
  updateIntakeFormFields: (
    fields?: IntakeFormFields,
    additionalQuestionAnswers?: AdditionalQuestionAnswer[],
  ) => Promise<void>;
  resolveFieldComment: (fieldId: string, comment: string) => Promise<void>;
  submitApplicationForReview: () => Promise<void>;
  activeLayout: LayoutValue;
  setActiveLayout: (layout: LayoutValue) => void;
  amendApplication: () => void;
  withdrawApplication: () => Promise<void>;
  acknowledgeDeactivation: () => Promise<void>;
  startOnboarding: () => Promise<void>;
  reviewComments: () => void;
  openedFieldIssuePopoverId: string | null;
  setOpenedFieldIssuePopoverId: (fieldId: string | null) => void;
  navigateToNextComment: () => void;
  navigateToComment: (step: string, fieldId: string) => void;
  haveNotResolvedComments: boolean;
  getActiveIssueByFieldId: (
    fieldId: string,
  ) => null | { isResolved: boolean; fieldIssue: FieldIssue };
  getSupplementaryQuestionsById: (
    formId: string,
  ) => SupplementaryQuestionsFormFromQuery | null;
}

function mapStepStatus(status: IntakeFormStepStatus): StepStatus {
  switch (status) {
    case 'incomplete':
      return 'new';
    case 'complete':
      return 'completed';
    case 'in_progress':
      return 'in-progress';
    case 'invalid':
      return 'invalid';
    default:
      return 'new';
  }
}

function getStatusScreenStep(status: IntakeFormStatus): string {
  switch (status) {
    case 'draft':
    case 'submitted':
      return 'submitted';
    case 'pending-approval':
    case 'ready-for-nbc':
    case 'support':
    case 'under-review':
      return 'under-review';
    case 'approved':
      return 'accepted';
    case 'archived':
      return 'archived';
    case 'declined':
      return 'declined';
    case 'returned':
      return 'returned';
    case 'withdrawn':
      return 'withdrawn';
    default:
      return 'submitted';
  }
}

function createStep(key: string, title: string, status: StepStatus): Step {
  return {
    key,
    title,
    status,
    disabled: true,
  };
}

function getDecisionStep(
  status: IntakeFormStatus,
  t: TFunction<string | string[], undefined>,
): Step | null {
  switch (status) {
    case 'submitted':
    case 'under-review':
    case 'pending-approval':
    case 'ready-for-nbc':
    case 'draft':
      return createStep('decision', t('intake_form.decision.title'), 'new');
    case 'approved':
      return createStep(
        'accepted',
        t('intake_form.accepted.title'),
        'completed',
      );
    case 'declined':
      return createStep(
        'declined',
        t('intake_form.declined.title'),
        'declined',
      );
    case 'archived':
      return createStep(
        'archived',
        t('intake_form.archived.title'),
        'archived',
      );
    case 'returned':
      return createStep(
        'returned',
        t('intake_form.returned.title'),
        'withdrawn',
      );
    case 'withdrawn':
      return createStep(
        'withdrawn',
        t('intake_form.withdrawn.title'),
        'withdrawn',
      );

    default:
      return null;
  }
}

function getStatusFormParentSection(
  statuses: IntakeFormStepStatus[],
): StepStatus {
  const notCompleted = statuses.find(
    (e) => e === 'incomplete' || e === 'in_progress',
  );

  if (notCompleted) {
    return 'new';
  } else {
    return 'completed';
  }
}

function patternToRegex(pattern: string): RegExp {
  const regexStr = pattern
    .replace(/\./g, '\\.')
    .replace(/\[\*\]/g, '(?:\\["[^"]+"\\]|\\.[^.]+)');
  return new RegExp(`^${regexStr}$`);
}

function normalizeDynamicPath(path: string): string {
  return path.replace(/\["[^"]+"\]/g, '[*]');
}

function extractGroupKey(path: string): string {
  // eslint-disable-next-line no-useless-escape
  const match = path.match(/^([^\.\[]+\[\*\])/);
  if (match) return match[1];
  return path.split('.')[0];
}

function extractSuffix(path: string): string {
  const parts = path.split('.');
  return parts.length > 1 ? parts[parts.length - 1] : '';
}

function extractFirstId(path: string): string {
  const match = path.match(/\["([^"]+)"\]/);
  return match?.[1] ?? '__static';
}

function getStepByFieldId(
  fieldId: string,
  steps: { step: string; fields: string[] }[],
): string | null {
  for (const step of steps) {
    for (const pattern of step.fields) {
      const regex = patternToRegex(pattern);
      if (regex.test(fieldId)) {
        return step.step;
      }
    }
  }
  return null;
}

function sortFieldsByStepOrder(
  fields: FieldIssueWithTemplate[],
  steps: {
    step: string;
    fields: string[];
  }[],
): FieldIssueWithTemplate[] {
  const flatFieldOrder = steps.flatMap((s) => s.fields);

  const groupOrderMap = new Map<string, number>();
  const fieldOrderInGroup = new Map<string, number>();

  for (let i = 0; i < flatFieldOrder.length; i++) {
    const fullField = flatFieldOrder[i];
    const groupKey = extractGroupKey(fullField);
    const fieldSuffix = extractSuffix(fullField);

    if (!groupOrderMap.has(groupKey)) {
      groupOrderMap.set(groupKey, groupOrderMap.size);
    }
    fieldOrderInGroup.set(`${groupKey}.${fieldSuffix}`, i);
  }

  const grouped: Record<string, Record<string, FieldIssueWithTemplate[]>> = {};

  for (const field of fields) {
    const groupKey = extractGroupKey(normalizeDynamicPath(field.fieldId));
    const id = extractFirstId(field.fieldId);
    if (!grouped[groupKey]) grouped[groupKey] = {};
    if (!grouped[groupKey][id]) grouped[groupKey][id] = [];
    grouped[groupKey][id].push(field);
  }

  const sorted: FieldIssueWithTemplate[] = [];

  const sortedGroups = Object.keys(grouped).sort(
    (a, b) => (groupOrderMap.get(a) ?? 9999) - (groupOrderMap.get(b) ?? 9999),
  );

  for (const groupKey of sortedGroups) {
    const group = grouped[groupKey];
    const sortedIds = Object.keys(group).sort();

    for (const id of sortedIds) {
      const items = group[id];

      items.sort((a, b) => {
        const aKey = `${groupKey}.${extractSuffix(normalizeDynamicPath(a.fieldId))}`;
        const bKey = `${groupKey}.${extractSuffix(normalizeDynamicPath(b.fieldId))}`;
        const aIndex = fieldOrderInGroup.get(aKey) ?? 9999;
        const bIndex = fieldOrderInGroup.get(bKey) ?? 9999;
        return aIndex - bIndex;
      });

      sorted.push(...items);
    }
  }

  return sorted;
}

const STEPS_WITH_ORDERED_FIELDS = [
  {
    step: 'entity_type',
    fields: ['entityCountry', 'entityTypeCategoryId'],
  },
  {
    step: 'basic_info',
    fields: [
      'entityLegalName',
      'entityDBA',
      'hearTenet',
      'referrerName',
      'referrerCompany',
      'referrerMore',
      'applicantRelations[*].relationshipTemplateId',
      'applicantRelations[*].additionalFields[*].valueJSON',
    ],
  },
  {
    step: 'entity_profile',
    fields: [
      'entityDateFormation',
      'entityOperationStart',
      'registrationNumber',
      'isRegulated',
      'regulationCountry',
      'regulationExemption',
      'hasBearerShares',
      'numEmployees',
      'numOffices',
      'isOperatingInSanctionedCountries',
      'entityWebsite',
      'addresses[*].type',
      'addresses[*].country',
      'addresses[*].city',
      'addresses[*].street',
      'addresses[*].state',
      'addresses[*].postalCode',
    ],
  },
  {
    step: 'customers_and_products',
    fields: [
      'industryCodes',
      'subIndustries',
      'isBlockchainTechnologyUsed',
      'isVirtualAssetsInvolvedInBusiness',
      'involvedVirtualAssets',
      'customerGroups',
      'customerGroupOtherDetails',
      'sourcesOfRevenue',
      'sourceOfRevenueOtherDetails',
      'businessDescription',
      'supportingDocuments',
      'projectedRevenue6m',
      'projectedRevenue6mAmount',
      'projectedRevenue12m',
      'projectedRevenue12mAmount',
      'projectedRevenue24m',
      'projectedRevenue24mAmount',
    ],
  },
  {
    step: 'funding',
    fields: [
      'counterpartyType',
      'thirdPartyName',
      'thirdPartyRelationship',
      'initialDeposit',
      'initialDepositAmount',
      'sourceOfFunds',
      'sourceOfFundsOtherDetails',
      'initialDepositCountries',
      'intendToTransferFundsFrame',
      'intendToTransferFundsFrameDetails',
      'hasOtherBanks',
      'otherBanks',
      'otherBankFeedback',
      'isPrimaryAccount',
      'accountUsePercentage',
      'plansToOpenExternalBankAccounts',
      'plansToOpenExternalBankAccountsDetails',
    ],
  },
  {
    step: 'account',
    fields: [
      'purposeOfAccount',
      'accountCountries',
      'incomingTransactionNumber',
      'incomingTransactionVolume',
      'outgoingTransactionNumber',
      'outgoingTransactionVolume',
      'avarageMonthlyBalance',
      'accountInflowsDetails',
      'accountOutflowsDetails',
    ],
  },
  {
    step: 'associated_individuals',
    fields: [
      'hasOtherBeneficialOwners',
      'beneficialOwners[*].fullName',
      'beneficialOwners[*].countryOfResidence',
      'beneficialOwners[*].relationshipToEntity',
    ],
  },
  {
    step: 'service_providers',
    fields: [
      'serviceProviders[*].serviceProviderId',
      'serviceProviders[*].additionalFields[*].valueJSON',
    ],
  },
  {
    step: SUPPLEMENTARY_QUESTIONS_STEP_KEY_BASE,
    fields: ['supplementaryQuestions.*[*].valueJSON'],
  },
];

// * Context
const EntityIntakeFormContext =
  React.createContext<EntityIntakeFormContextData>(
    {} as EntityIntakeFormContextData,
  );

// * Provider
export const EntityIntakeFormContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const intakeFormId = useSelector<StateModel, string | null>(
    (state) => state.intakeForm.intakeFormId,
  );

  // ---------- States-------------
  const [activeLayout, setActiveLayout] = useState<LayoutValue>(null);
  const [activeStepKey, setActiveStepKey] = useState<string | null>(null);
  const [loadingIntakeForm, setLoadingIntakeForm] = useState(false);
  const [intakeForm, setIntakeForm] = useState<IntakeFormFromQuery | null>(
    null,
  );
  const [activeFieldIssues, setActiveFieldIssues] = useState<
    IntakeFormFromQuery['fieldIssues'] | null
  >(null);
  const [openedFieldIssuePopoverId, setOpenedFieldIssuePopoverId] = useState<
    string | null
  >(null);
  const [pendingScrollTarget, setPendingScrollTarget] = useState<string | null>(
    null,
  );

  // ---------- Memo variables -------------
  const steps = useMemo<Step[]>(() => {
    if (!intakeForm || !activeLayout) {
      return [];
    }

    if (activeLayout === 'intake-form') {
      const isEntityTypeStepIncomplete =
        intakeForm.completionStatuses.entityType === 'incomplete';
      let intakeFormSteps: Step[] = [
        {
          key: 'entity_type',
          title: t('intake_form.entity_type.title'),
          status: mapStepStatus(intakeForm.completionStatuses.entityType),
          disabled: false,
        },

        {
          key: 'entity_details',
          title: t('intake_form.entity_details.title'),
          status: getStatusFormParentSection([
            intakeForm.completionStatuses.basicInfo,
            intakeForm.completionStatuses.entityProfile,
            intakeForm.completionStatuses.customerAndProducts,
          ]),
          disabled: true,
          children: [
            {
              key: 'basic_info',
              title: t('intake_form.basic_info.title'),
              status: mapStepStatus(intakeForm.completionStatuses.basicInfo),
              disabled: isEntityTypeStepIncomplete,
            },

            {
              key: 'entity_profile',
              title: t('intake_form.entity_profile.title'),
              status: mapStepStatus(
                intakeForm.completionStatuses.entityProfile,
              ),
              disabled: isEntityTypeStepIncomplete,
            },

            {
              key: 'customers_and_products',
              title: t('intake_form.customers_and_products.title'),
              status: mapStepStatus(
                intakeForm.completionStatuses.customerAndProducts,
              ),
              disabled: isEntityTypeStepIncomplete,
            },
          ],
        },

        {
          key: 'finances',
          title: t('intake_form.finances.title'),
          status: getStatusFormParentSection([
            intakeForm.completionStatuses.funding,
            intakeForm.completionStatuses.account,
          ]),
          disabled: true,
          children: [
            {
              key: 'funding',
              title: t('intake_form.funding.title'),
              status: mapStepStatus(intakeForm.completionStatuses.funding),
              disabled: isEntityTypeStepIncomplete,
            },
            {
              key: 'account',
              title: t('intake_form.account.title'),
              status: mapStepStatus(intakeForm.completionStatuses.account),
              disabled: isEntityTypeStepIncomplete,
            },
          ],
        },

        {
          key: 'associates',
          title: t('intake_form.associates.title'),
          status: getStatusFormParentSection([
            intakeForm.completionStatuses.associatedIndividuals,
            intakeForm.completionStatuses.serviceProviders,
          ]),
          disabled: true,
          children: [
            {
              key: 'associated_individuals',
              title: t('intake_form.associated_individuals.title'),
              status: mapStepStatus(
                intakeForm.completionStatuses.associatedIndividuals,
              ),
              disabled: isEntityTypeStepIncomplete,
            },

            {
              key: 'service_providers',
              title: t('intake_form.service_providers.title'),
              status: mapStepStatus(
                intakeForm.completionStatuses.serviceProviders,
              ),
              disabled: isEntityTypeStepIncomplete,
            },
          ],
        },
      ];

      if (
        Array.isArray(intakeForm.additionalQuestions) &&
        intakeForm.additionalQuestions.length
      ) {
        intakeFormSteps = [
          ...intakeFormSteps,
          {
            disabled: false,
            key: 'additional_questions',
            title: t('intake_form.additional_questions.title'),
            status: mapStepStatus(
              intakeForm.completionStatuses.additionalQuestions,
            ),
          },
        ];
      }

      if (intakeForm.triggeredSupplementaryQuestionForms?.length) {
        intakeFormSteps = [
          ...intakeFormSteps,
          {
            disabled: true,
            key: 'supplementary_questions',
            title: t('intake_form.supplementary_questions.title'),
            status: mapStepStatus(
              intakeForm.completionStatuses.supplementaryQuestions,
            ),
            children: intakeForm.triggeredSupplementaryQuestionForms.map(
              (e) => ({
                disabled: isEntityTypeStepIncomplete,
                key: `${SUPPLEMENTARY_QUESTIONS_STEP_KEY_BASE}${e._id}`,
                title: e.name,
                status: mapStepStatus(e.status),
              }),
            ),
          },
        ];
      }

      return [
        ...intakeFormSteps,
        {
          key: 'review',
          title: t('intake_form.review_submit.title'),
          status: intakeForm.state !== 'draft' ? 'completed' : 'new',
          disabled: isEntityTypeStepIncomplete,
        },
      ];
    }

    if (activeLayout === 'status') {
      const statusOfUnderReviewStep: StepStatus = [
        'under-review',
        'pending-approval',
        'ready-for-nbc',
        'support',
      ].includes(intakeForm.state)
        ? 'under-review'
        : intakeForm.state == 'submitted'
          ? 'new'
          : 'completed';

      const statusSteps: Step[] = [
        {
          key: 'submitted',
          title: t('intake_form.submitted.title'),
          status: 'completed',
          disabled: true,
        },
        {
          key: 'under-review',
          title: t('intake_form.under_review.title'),
          status: statusOfUnderReviewStep,
          disabled: true,
        },
      ];

      const decisionStep = getDecisionStep(intakeForm.state, t);
      if (decisionStep) {
        statusSteps.push(decisionStep);
      }

      return statusSteps;
    }

    return [];
  }, [intakeForm, activeLayout]);

  const clickableSteps = useMemo(() => {
    if (!steps.length) {
      return [];
    }

    return steps.flatMap((step) =>
      step.children && step.children.length ? step.children : [step],
    );
  }, [steps]);

  const haveNotResolvedComments = useMemo(() => {
    if (!activeFieldIssues) {
      return false;
    }

    return !!Object.keys(activeFieldIssues).length;
  }, [activeFieldIssues]);

  // -------- Methods --------
  const selectStep = (newStepKey: string) => {
    dispatch(
      changeView(() => {
        setActiveStepKey(newStepKey);
      }, CHANGE_VIEW_TYPES.TABS),
    );
  };

  const fetchAndStoreIntakeForm = useCallback(async () => {
    if (!intakeFormId) return;
    setLoadingIntakeForm(true);
    try {
      const response = await intakeFormAPI.fetchIntakeFormById(intakeFormId);
      const adaptedResponse = crmAdapter.fetchAndStoreIntakeForm(response);
      setIntakeForm(adaptedResponse.clientIntakeSubmission);
      setActiveLayout(
        response.clientIntakeSubmission.state === 'draft'
          ? 'intake-form'
          : 'status',
      );
    } catch (err) {
      ErrorHandlerService.handleError(err as any);
    } finally {
      setLoadingIntakeForm(false);
    }
  }, [intakeFormId]);

  const navigateToNextStep = useCallback(async () => {
    if (activeStepKey && clickableSteps.length) {
      const indexOfSelectedStep = clickableSteps.findIndex(
        (e) => e.key === activeStepKey,
      );

      if (indexOfSelectedStep === clickableSteps.length - 1) {
        return;
      }

      if (indexOfSelectedStep !== -1) {
        selectStep(clickableSteps[indexOfSelectedStep + 1].key);
      } else {
        selectStep(clickableSteps[0].key);
      }
    }
  }, [activeStepKey, clickableSteps]);

  const navigateToPreviousStep = useCallback(async () => {
    if (activeStepKey && clickableSteps.length) {
      const indexOfSelectedStep = clickableSteps.findIndex(
        (e) => e.key === activeStepKey,
      );

      if (indexOfSelectedStep !== -1) {
        selectStep(clickableSteps[indexOfSelectedStep - 1].key);
      } else {
        selectStep(clickableSteps[0].key);
      }
    }
  }, [activeStepKey, clickableSteps]);

  const updateIntakeFormFields = useCallback(
    async (
      fields?: IntakeFormFields,
      additionalQuestionAnswers?: AdditionalQuestionAnswer[],
    ) => {
      if (!intakeForm) {
        return;
      }
      const response = await intakeFormAPI.setIntakeFormFields(intakeForm._id, {
        fields,
        additionalQuestionAnswers,
      });
      Message.success(t('success_save', { ns: 'common' }));
      setIntakeForm(response);
    },
    [intakeForm],
  );

  const resolveFieldComment = useCallback(
    async (fieldId: string, comment: string) => {
      if (!intakeForm) {
        return;
      }
      const response = await intakeFormAPI.setIntakeFormFields(intakeForm._id, {
        fieldIssuesToResolve: [{ fieldId, comment }],
      });
      Message.success(t('success_save', { ns: 'common' }));
      setIntakeForm(response);
    },
    [intakeForm],
  );

  const submitApplicationForReview = useCallback(async () => {
    if (!intakeForm) {
      return;
    }

    if (intakeForm.state === 'draft') {
      try {
        const response = await intakeFormAPI.submitIntakeFormForReview(
          intakeForm._id,
        );
        setIntakeForm((prev) =>
          prev ? { ...prev, state: 'submitted' } : null,
        );
        const newLayout =
          response.state == 'submitted' ? 'status' : 'intake-form';
        setActiveLayout(newLayout);
        if (newLayout === 'status') {
          setActiveStepKey('submitted');
        }
      } catch (err) {
        ErrorHandlerService.handleError(err as any);
      }
    } else {
      setActiveLayout('status');
      setActiveStepKey('submitted');
    }
  }, [intakeForm, activeLayout]);

  const withdrawApplication = useCallback(async () => {
    if (!intakeForm) {
      return;
    }

    try {
      const response = await intakeFormAPI.withdrawApplication(intakeForm._id);
      setIntakeForm(response);
      setActiveStepKey(getStatusScreenStep(response.state));
    } catch (err) {
      ErrorHandlerService.handleError(err as any);
    }
  }, [intakeForm]);

  const amendApplication = useCallback(() => {
    setActiveLayout('intake-form');
    setActiveStepKey('review');
  }, []);

  const reviewComments = useCallback(() => {
    setActiveLayout('intake-form');
    setActiveStepKey('review');
  }, []);

  const getSupplementaryQuestionsById = useCallback(
    (formId: string) => {
      if (!intakeForm) {
        return null;
      }

      return (
        intakeForm.triggeredSupplementaryQuestionForms.find(
          (e) => e._id == formId,
        ) || null
      );
    },
    [intakeForm],
  );

  const getActiveIssueByFieldId = useCallback(
    (fieldId: string) => {
      if (
        !intakeForm ||
        !intakeForm.fieldIssues[fieldId] ||
        !intakeForm.fieldIssues[fieldId].active
      ) {
        return null;
      }
      const issue = intakeForm.fieldIssues[fieldId].active;
      return {
        isResolved: issue.isResolved,
        fieldIssue: issue,
      };
    },
    [intakeForm],
  );

  const acknowledgeDeactivation = useCallback(async () => {
    if (!intakeForm) {
      return;
    }

    try {
      await intakeFormAPI.deactivateApplication(intakeForm._id);
      dispatch(signOut());
    } catch (err) {
      ErrorHandlerService.handleError(err as any);
    }
  }, [intakeForm]);

  const startOnboarding = useCallback(async () => {
    if (!intakeForm?.metadata.createdApplicationId) {
      return;
    }
    const response = await applicationsAPI.startOnboarding(
      intakeForm.metadata.createdApplicationId,
    );

    if (response) {
      dispatch(setActiveApplication(response));
      dispatch(
        selectApplication(response, true, false, () => {
          navigate(RoutePaths.OnboardingPage);
        }),
      );
    }
  }, [intakeForm]);

  const getSupplementaryQuestionId = (supplementaryQuestionKey: string) => {
    // Extracts the first supplementary question ID from nextFieldIssueKey
    // (e.g. "supplementaryQuestions.68dfdc70f542e0c19e0ed5ec[...]")
    const supplementaryQuestionResult =
      /(?<=supplementaryQuestions\.)[a-f0-9]+(?=\[)/.exec(
        supplementaryQuestionKey,
      );

    return supplementaryQuestionResult ? supplementaryQuestionResult[0] : '';
  };

  const navigateToNextComment = useCallback(async () => {
    if (!activeFieldIssues || !openedFieldIssuePopoverId) {
      return;
    }

    const fieldIssueKeys = Object.keys(activeFieldIssues || {});
    const openedFieldIssueIndex = fieldIssueKeys.indexOf(
      openedFieldIssuePopoverId as string,
    );

    let nextFieldIssueKey = null;
    if (openedFieldIssueIndex + 1 == fieldIssueKeys.length) {
      nextFieldIssueKey = fieldIssueKeys[0];
    } else {
      nextFieldIssueKey = fieldIssueKeys[openedFieldIssueIndex + 1];
    }

    let nextFieldIssueStep = getStepByFieldId(
      nextFieldIssueKey,
      STEPS_WITH_ORDERED_FIELDS,
    );

    if (activeStepKey !== nextFieldIssueStep) {
      if (nextFieldIssueStep === SUPPLEMENTARY_QUESTIONS_STEP_KEY_BASE) {
        // Extracts the first supplementary question ID from nextFieldIssueKey
        // (e.g. "supplementaryQuestions.68dfdc70f542e0c19e0ed5ec[...]")
        const supplementaryQuestionResult =
          /(?<=supplementaryQuestions\.)[a-f0-9]+(?=\[)/.exec(
            nextFieldIssueKey,
          );
        if (supplementaryQuestionResult) {
          nextFieldIssueStep =
            SUPPLEMENTARY_QUESTIONS_STEP_KEY_BASE +
            supplementaryQuestionResult[0];
        }
      }

      dispatch(
        changeView(() => {
          setActiveStepKey(nextFieldIssueStep);
          setPendingScrollTarget(nextFieldIssueKey);
        }),
      );
    } else {
      setPendingScrollTarget(nextFieldIssueKey);
    }
  }, [activeFieldIssues, openedFieldIssuePopoverId, activeStepKey]);

  const navigateToComment = (step: string, fieldId: string) => {
    let fieldIssueStep = step;

    if (fieldIssueStep === SUPPLEMENTARY_QUESTIONS_STEP_KEY_BASE) {
      const supplementaryQuestionResult =
        /(?<=supplementaryQuestions\.)[a-f0-9]+(?=\[)/.exec(fieldId);
      if (supplementaryQuestionResult) {
        fieldIssueStep =
          SUPPLEMENTARY_QUESTIONS_STEP_KEY_BASE +
          supplementaryQuestionResult[0];
      }
    }
    dispatch(
      changeView(() => {
        setActiveStepKey(fieldIssueStep);
        setPendingScrollTarget(fieldId);
      }),
    );
  };

  // --------- Use effects ----------
  useEffect(() => {
    // Set initial step
    if (!activeStepKey && clickableSteps.length && intakeForm?.state) {
      const firstIncomplete = clickableSteps.find(
        (step) => step.status !== 'completed',
      );

      if (activeLayout === 'status') {
        selectStep(getStatusScreenStep(intakeForm.state));
      } else {
        selectStep(
          firstIncomplete
            ? firstIncomplete.key
            : clickableSteps[clickableSteps.length - 1].key,
        );
      }
    }
  }, [activeStepKey, clickableSteps, activeLayout, intakeForm]);

  // Scroll and open field issue popover
  useEffect(() => {
    if (!pendingScrollTarget) return;

    const el = document.querySelector(
      `#field-${pendingScrollTarget.replace(/[^a-zA-Z0-9_-]/g, '_')}`,
    );
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setOpenedFieldIssuePopoverId(pendingScrollTarget);
      setPendingScrollTarget(null);
    }
  }, [activeStepKey, pendingScrollTarget]);

  function convertFieldIssuesToFieldIssueWithTemplateArray(
    fieldIssues: Record<
      string,
      {
        active: FieldIssue | null;
        resolved: FieldIssue[] | null;
        fieldTemplate: FieldTemplate | null;
      }
    >,
  ): FieldIssueWithTemplate[] {
    const result: FieldIssueWithTemplate[] = [];

    for (const key of Object.keys(fieldIssues)) {
      const item = fieldIssues[key];

      if (!item.fieldTemplate) continue;

      const template = {
        template: item.fieldTemplate.template ?? '',
        values: item.fieldTemplate.values ?? [],
      };

      if (item.active) {
        result.push({
          ...item.active,
          fieldTemplate: template,
        });
      }

      if (item.resolved?.length) {
        for (const r of item.resolved) {
          result.push({
            ...r,
            fieldTemplate: template,
          });
        }
      }
    }

    return result;
  }

  useEffect(() => {
    if (!intakeForm && activeFieldIssues) {
      setActiveFieldIssues(null);
    } else if (intakeForm?.fieldIssues) {
      const activeFieldIssueKeys = Object.keys(intakeForm.fieldIssues).filter(
        (key) => !intakeForm.fieldIssues[key].active?.isResolved,
      );
      if (activeFieldIssueKeys.length) {
        const sortedFieldIssues = sortFieldsByStepOrder(
          convertFieldIssuesToFieldIssueWithTemplateArray(
            intakeForm.fieldIssues,
          ).filter((x) => !x.isResolved),
          STEPS_WITH_ORDERED_FIELDS,
        );

        const sortedFieldIssuesMapped = sortedFieldIssues.reduce<
          IntakeFormFromQuery['fieldIssues']
        >((acc, next) => {
          if (intakeForm.fieldIssues[next.fieldId]) {
            acc[next.fieldId] = { ...intakeForm.fieldIssues[next.fieldId] };
          }
          return acc;
        }, {});
        setActiveFieldIssues(
          Object.keys(sortedFieldIssuesMapped).length
            ? sortedFieldIssuesMapped
            : null,
        );
      } else if (activeFieldIssues) {
        setActiveFieldIssues(null);
      }
    }
  }, [intakeForm]);

  const applicationReview = useMemo<ApplicationReviewModel | null>(() => {
    if (intakeForm?.fieldIssues) {
      // Extract and flatten all comments (active + resolved) from intakeForm.fieldIssues
      // Each comment is combined with its corresponding field template data
      const allFieldIssues = Object.values(intakeForm.fieldIssues).flatMap(
        (x) => {
          if (!x.fieldTemplate) return [];

          const fieldTemplate = {
            template: x.fieldTemplate.template ?? '',
            values: x.fieldTemplate.values ?? [],
          };

          const issues: FieldIssueWithTemplate[] = [];

          if (x.active) issues.push({ ...x.active, fieldTemplate });
          if (x.resolved?.length) {
            issues.push(...x.resolved.map((r) => ({ ...r, fieldTemplate })));
          }

          return issues;
        },
      );

      // Sort all comment field IDs according to the predefined step order
      const sortedAllFieldIssues = sortFieldsByStepOrder(
        allFieldIssues,
        STEPS_WITH_ORDERED_FIELDS,
      );

      // Map sorted field IDs to a structured comment model
      // Each item represents a form step with its corresponding comments
      const structuredFieldComments = sortedAllFieldIssues.reduce<
        FieldCommentModel[]
      >((acc, next) => {
        const comment = allFieldIssues.find((x) => x.id === next.id);

        const fieldIssueStep = getStepByFieldId(
          next.fieldId,
          STEPS_WITH_ORDERED_FIELDS,
        );

        let supplementaryQuestionId = null;

        if (fieldIssueStep === SUPPLEMENTARY_QUESTIONS_STEP_KEY_BASE) {
          supplementaryQuestionId = getSupplementaryQuestionId(next.fieldId);
        }

        if (comment && fieldIssueStep) {
          acc.push({
            stepName: supplementaryQuestionId
              ? fieldIssueStep + supplementaryQuestionId
              : fieldIssueStep,
            fieldComments: [
              {
                step: fieldIssueStep,
                clientComment: comment.resolvedWithComment,
                reviewerComment: comment.comment,
                field: renderTemplate(
                  comment.fieldTemplate.template,
                  comment.fieldTemplate.values,
                  TEMPLATE_FIELD_LABELS,
                ),
                fieldId: comment.fieldId,
                status: comment.isResolved
                  ? ApplicationCommentsStatus.Resolved
                  : ApplicationCommentsStatus.Open,
              },
            ],
          });
        }
        return acc;
      }, []);

      // Group all comments by step name, merging comments from the same step
      const groupedFieldComments = structuredFieldComments.reduce<
        Record<string, { stepName: string; fieldComments: FieldIssueModel[] }>
      >((acc, curr) => {
        if (!acc[curr.stepName]) {
          acc[curr.stepName] = {
            stepName: curr.stepName,
            fieldComments: [],
          };
        }
        acc[curr.stepName].fieldComments.push(...curr.fieldComments);
        return acc;
      }, {});

      // Transform grouped comments for display
      // If the field belongs to supplementary questions — split it into subgroups based on the " - " separator
      // Otherwise, just localize the field name
      const localizedAndGroupedComments = Object.values(
        groupedFieldComments,
      ).flatMap((item) => {
        if (!item.stepName.includes(SUPPLEMENTARY_QUESTIONS_STEP_KEY_BASE)) {
          const updatedItem = {
            ...item,
            stepName: t(`intake_form.${item.stepName}.title`),
          };
          return [updatedItem];
        }

        const parts = item.stepName.split('-');
        const supplementaryQuestionId = parts[1];
        const supplementaryQuestion =
          intakeForm.triggeredSupplementaryQuestionForms.find(
            (x) => x._id === supplementaryQuestionId,
          );
        const fieldIssue = {
          ...item,
          stepName: supplementaryQuestion?.name ?? '',
        };
        return [fieldIssue];
      });

      // Calculate total, active, and resolved comment counts
      // Build the final application review object that aggregates all comment data
      const applicationReviewSummary: ApplicationReviewModel = Object.values(
        groupedFieldComments,
      ).reduce(
        (acc, issue) => {
          issue.fieldComments.forEach((comment) => {
            acc.allCommentsCount++;

            if (comment.status === ApplicationCommentsStatus.Open) {
              acc.activeCommentsCount++;
            } else if (comment.status === ApplicationCommentsStatus.Resolved) {
              acc.resolvedCommentsCount++;
            }
          });

          return acc;
        },
        {
          fieldIssues: localizedAndGroupedComments,
          activeCommentsCount: 0,
          resolvedCommentsCount: 0,
          allCommentsCount: 0,
        },
      );

      return applicationReviewSummary;
    }

    return null;
  }, [intakeForm]);

  const value = useMemo(() => {
    return {
      steps,
      activeStepKey,
      selectStep,
      intakeForm,
      setIntakeForm,
      applicationReview,
      loadingIntakeForm,
      fetchAndStoreIntakeForm,
      navigateToNextStep,
      navigateToPreviousStep,
      updateIntakeFormFields,
      submitApplicationForReview,
      activeLayout,
      setActiveLayout,
      amendApplication,
      withdrawApplication,
      acknowledgeDeactivation,
      startOnboarding,
      reviewComments,
      getActiveIssueByFieldId,
      haveNotResolvedComments,
      openedFieldIssuePopoverId,
      setOpenedFieldIssuePopoverId,
      resolveFieldComment,
      navigateToComment,
      navigateToNextComment,
      getSupplementaryQuestionsById,
    };
  }, [
    steps,
    activeStepKey,
    selectStep,
    intakeForm,
    setIntakeForm,
    applicationReview,
    loadingIntakeForm,
    fetchAndStoreIntakeForm,
    navigateToNextStep,
    navigateToPreviousStep,
    updateIntakeFormFields,
    submitApplicationForReview,
    activeLayout,
    setActiveLayout,
    amendApplication,
    withdrawApplication,
    acknowledgeDeactivation,
    startOnboarding,
    reviewComments,
    getActiveIssueByFieldId,
    haveNotResolvedComments,
    openedFieldIssuePopoverId,
    setOpenedFieldIssuePopoverId,
    resolveFieldComment,
    navigateToComment,
    navigateToNextComment,
    getSupplementaryQuestionsById,
  ]);
  return (
    <EntityIntakeFormContext.Provider value={value}>
      {children}
    </EntityIntakeFormContext.Provider>
  );
};

// * Hook
export function useEntityIntakeFormContext(): EntityIntakeFormContextData {
  const context = useContext(EntityIntakeFormContext);
  return context;
}
